import React, { useState, useRef } from "react";
import api from "../lib/api";
import { fmtDate } from "../lib/helpers";
import COLORS from "../lib/colors";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../lib/icons";
import AuthFileLink from "./AuthFileLink";
import { useConfirm } from "./ConfirmDialog";

const VIS = {
  collaborator: { label: "Collaborateur", icon: "User", color: COLORS.marine },
  admin: { label: "Admin uniquement", icon: "Lock", color: COLORS.bordeaux },
  client: { label: "Entreprise", icon: "Building2", color: COLORS.bleu },
};

const FileRow = ({ mid, f, bucket, canClient, reload }) => {
  const { confirm } = useConfirm();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(f.name || "");
  const [desc, setDesc] = useState(f.description || "");
  const [showHistory, setShowHistory] = useState(false);
  const replaceRef = useRef();
  const vmeta = VIS[f.visibility] || VIS.collaborator;
  const VIc = ICONS_BY_NAME[vmeta.icon] || Icons.User;

  const patch = async (payload) => { try { await api.put(`/admin/missions/${mid}/files/${f.id}`, payload); reload(); } catch { toast.error("Erreur"); } };
  const saveEdit = async () => { await patch({ name, description: desc }); setEditing(false); toast.success("Fichier mis à jour"); };
  const del = async () => { if (!(await confirm({ title: `Supprimer « ${f.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; await api.delete(`/admin/missions/${mid}/files/${f.id}`); toast.success("Fichier supprimé"); reload(); };
  const replace = async (e) => { const file = e.target.files[0]; if (!file) return; const fd = new FormData(); fd.append("file", file); try { await api.post(`/admin/missions/${mid}/files/${f.id}/replace`, fd); toast.success("Nouvelle version enregistrée"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); } finally { e.target.value = ""; } };

  return (
    <div className="border border-marine/10 rounded-sm p-3 mb-2" data-testid={`mfile-${f.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {bucket === "shared" ? <Icons.File size={16} className="text-marine shrink-0" /> : <Icons.FileCheck size={16} className="text-bordeaux shrink-0" />}
            {editing
              ? <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`mfile-name-${f.id}`} className="text-sm border border-marine/30 rounded-sm px-2 py-0.5 flex-1" />
              : <span className="text-sm font-medium text-marine truncate">{f.name}</span>}
            {f.version > 1 && <span className="text-[9px] text-marine/80 bg-marine-50 border border-marine/30 px-1.5 py-0.5 rounded-sm shrink-0">v{f.version}</span>}
          </div>
          {editing
            ? <input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid={`mfile-desc-${f.id}`} placeholder="Description (facultatif)" className="mt-1.5 w-full text-xs border border-marine/20 rounded-sm px-2 py-1" />
            : (f.description && <div className="text-xs text-marine/80 mt-0.5">{f.description}</div>)}
          <div className="text-xs text-marine/80 mt-1">{f.uploaded_by_name || "Metara"} · {fmtDate(f.uploaded_at)}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {f.path && <AuthFileLink path={f.path} title="Télécharger" data-testid={`mfile-dl-${f.id}`} className="p-1.5 text-marine hover:bg-marine-50 rounded-sm inline-flex"><Icons.Download size={16} /></AuthFileLink>}
          <label title="Remplacer (nouvelle version)" className="p-1.5 text-marine hover:bg-marine-50 rounded-sm cursor-pointer"><Icons.RefreshCw size={16} /><input ref={replaceRef} type="file" className="hidden" onChange={replace} data-testid={`mfile-replace-${f.id}`} /></label>
          {editing
            ? <button onClick={saveEdit} title="Enregistrer" data-testid={`mfile-save-${f.id}`} className="p-1.5 text-bleu hover:bg-bleu-50 rounded-sm"><Icons.Check size={16} /></button>
            : <button onClick={() => setEditing(true)} title="Renommer / décrire" data-testid={`mfile-edit-${f.id}`} className="p-1.5 text-marine hover:bg-marine-50 rounded-sm"><Icons.Pencil size={16} /></button>}
          <button onClick={del} title="Supprimer" data-testid={`mfile-del-${f.id}`} className="p-1.5 text-rouge hover:bg-rouge-50 rounded-sm"><Icons.Trash2 size={16} /></button>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="text-[11px] text-marine/80">Visibilité :</span>
        <select aria-label={`Visibilité de ${f.name}`} value={f.visibility} onChange={(e) => patch({ visibility: e.target.value })} data-testid={`mfile-vis-${f.id}`} className="text-xs border rounded-sm px-1.5 py-1" style={{ borderColor: `${vmeta.color}55`, color: vmeta.color }}>
          <option value="collaborator">{VIS.collaborator.label}</option>
          <option value="admin">{VIS.admin.label}</option>
          <option value="client" disabled={!canClient}>{VIS.client.label}{canClient ? "" : " (liez une entreprise/projet)"}</option>
        </select>
        <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm border" style={{ background: `${vmeta.color}15`, color: vmeta.color, borderColor: `${vmeta.color}4D` }}>{vmeta.label}</span>
        {(f.versions || []).length > 0 && <button onClick={() => setShowHistory(!showHistory)} data-testid={`mfile-history-${f.id}`} className="text-[11px] text-marine hover:underline ml-auto flex items-center gap-1"><Icons.History size={16} /> {f.versions.length} version(s) précédente(s)</button>}
      </div>
      {showHistory && (
        <div className="mt-2 pl-3 border-l-2 border-marine/15 space-y-1" data-testid={`mfile-versions-${f.id}`}>
          {(f.versions || []).map((v, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] text-marine/80">
              <span>v{v.version} · {v.name} · {v.uploaded_by_name} · {fmtDate(v.uploaded_at)}</span>
              {v.path && <AuthFileLink path={v.path} className="text-marine inline-flex"><Icons.Download size={16} /></AuthFileLink>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MissionFiles = ({ mission, bucket, files, reload }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const mid = mission.id;
  const canClient = !!(mission.client_id || mission.project_id);
  const isShared = bucket === "shared";
  const defaultVis = "collaborator";

  const add = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("bucket", isShared ? "shared" : "deliverable");
      fd.append("visibility", defaultVis);
      list.forEach((file) => fd.append("files", file));
      await api.post(`/admin/missions/${mid}/files`, fd);
      toast.success(`${list.length} fichier(s) ajouté(s)`);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de l'import"); } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-xs uppercase tracking-wider text-bordeaux">{isShared ? "Fichiers partagés par METARA" : "Livrables reçus"}</div>
        <label data-testid={`mfiles-add-${bucket}`} className="text-xs bg-marine text-white rounded-sm px-2.5 py-1.5 hover:bg-marine-hover transition-colors cursor-pointer inline-flex items-center gap-1">
          <Icons.Upload size={16} /> {uploading ? "Import..." : "Ajouter des fichiers"}
          <input ref={inputRef} type="file" multiple className="hidden" onChange={add} disabled={uploading} />
        </label>
      </div>
      {(files || []).length === 0 && <p className="text-sm text-marine/80 mb-2">Aucun fichier.</p>}
      {(files || []).map((f) => <FileRow key={f.id} mid={mid} f={f} bucket={bucket} canClient={canClient} reload={reload} />)}
    </div>
  );
};
